import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Cpu, Network, Shield, Zap, BarChart3 } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const INDUSTRIES = [
  'Government', 'Defense', 'Energy', 'Oil & Gas', 'Railways', 'Satellite', 'Education', 'Drones & UV', 'Civilian'
];

const SERVICES = [
  {
    icon: Brain,
    title: 'Artificial Intelligence',
    desc: 'End-to-end AI strategy, from ideation to production-grade deployment across your enterprise.',
    span: 'md:col-span-8'
  },
  {
    icon: Network,
    title: 'Deep Learning',
    desc: 'Custom neural architectures for computer vision, NLP, and signal processing.',
    span: 'md:col-span-4'
  },
  {
    icon: Cpu,
    title: 'Machine Learning',
    desc: 'Scalable ML pipelines that turn raw data into actionable intelligence.',
    span: 'md:col-span-4'
  },
  {
    icon: Shield,
    title: 'Defense-Grade AI',
    desc: 'Secure, classified AI systems built to military specifications and compliance standards.',
    span: 'md:col-span-8'
  }
];

const STATS = [
  { value: '150+', label: 'Enterprise Clients' },
  { value: '$4.2B', label: 'Client Savings Generated' },
  { value: '97.8%', label: 'Model Accuracy Average' },
  { value: '47', label: 'Countries Deployed' }
];

export default function HomePage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handler = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[85vh] md:min-h-screen flex items-start overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div
          className="absolute w-[800px] h-[800px] rounded-full opacity-20 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.3) 0%, transparent 70%)',
            left: mousePos.x - 400,
            top: mousePos.y - 400,
          }}
        />

        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 pt-28 md:pt-40 pb-16 md:pb-24">
          <div className="max-w-4xl">
            <motion.p
              {...fadeIn}
              className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-8"
            >
              AI / ML / Deep Learning Solutions
            </motion.p>

            <motion.h1
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
            >
              Intelligence
              <br />
              <span className="text-gradient-cyan">Engineered</span> for
              <br />
              Enterprise, Defense
              <br />
              & Education
            </motion.h1>

            <motion.p
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.2 }}
              className="text-base md:text-xl text-slate-400 font-light leading-relaxed max-w-xl mb-8 md:mb-12"
            >
              We help traditional companies transition to the era of AI. From defense to energy, our solutions transform how industries operate.
            </motion.p>

            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-8 py-4 hover:bg-slate-200 transition-colors duration-200 w-full sm:w-auto"
              >
                Start a Project <ArrowRight size={16} />
              </Link>
              <Link
                to="/case-studies"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-bold text-sm uppercase tracking-wider px-8 py-4 hover:border-white hover:bg-white/5 transition-colors duration-200 backdrop-blur-sm w-full sm:w-auto"
              >
                View Our Work
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Mandala Hero Image */}
        <div className="absolute right-[-15%] md:right-[-5%] lg:right-[5%] top-[45%] -translate-y-1/2 w-[350px] h-[350px] md:w-[550px] md:h-[550px] lg:w-[700px] lg:h-[700px] pointer-events-none">
          <motion.img
            src="/logo-white.png"
            alt=""
            className="w-full h-full object-contain opacity-[0.12] md:opacity-[0.15]"
            animate={{
              scale: [1, 1.06, 1],
              opacity: [0.12, 0.18, 0.12],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      </section>

      {/* Client Ticker */}
      <section data-testid="clients-ticker" className="border-y border-white/5 py-6 overflow-hidden">
        <div className="ticker-track">
          {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => (
            <span key={i} className="flex items-center gap-8 px-8">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-600 font-bold whitespace-nowrap">{ind}</span>
              <span className="w-1 h-1 bg-slate-700 rounded-full" />
            </span>
          ))}
        </div>
      </section>

      {/* Services Bento Grid */}
      <section data-testid="services-section" className="py-16 md:py-32 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">What We Build</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight">
              Solutions That <span className="text-gradient-cyan">Scale</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {SERVICES.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`glass-card rounded-lg p-8 lg:p-10 group cursor-pointer ${service.span}`}
              >
                <service.icon className="w-8 h-8 text-brand-accent mb-6 opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl font-heading font-semibold mb-3">{service.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-12">
            <Link
              to="/services"
              data-testid="services-link"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm uppercase tracking-widest transition-colors duration-200 hover:underline underline-offset-8"
            >
              Explore All Services <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats-section" className="py-14 md:py-24 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-left"
              >
                <p className="text-4xl md:text-5xl font-heading font-black stat-value text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-16 md:py-32 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
              Ready to Transform
              <br />
              Your Enterprise?
            </h2>
            <p className="text-lg text-slate-400 font-light max-w-lg mx-auto mb-12">
              Let&apos;s discuss how AI can solve your most complex challenges.
            </p>
            <Link
              to="/contact"
              data-testid="cta-button"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200"
            >
              Start the Conversation <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
